import { useRef } from 'react';
import { motion } from 'framer-motion';
import { useFigurines, useCategories } from '@/hooks/useFigurines';
import FigurineCard from './FigurineCard';
import CategoryFilter from './CategoryFilter';
import { useState } from 'react';
import { Skeleton } from '@/components/ui/skeleton';
import { Package } from 'lucide-react';

interface FigurineGridProps {
  onAuthRequired: () => void;
}

function FigurineSkeletonCard() {
  return (
    <div
      className="rounded-2xl border overflow-hidden"
      style={{ background: 'oklch(0.16 0.03 280)', borderColor: 'oklch(0.25 0.045 280)' }}
    >
      <div className="h-52 relative overflow-hidden" style={{ background: 'oklch(0.13 0.028 280)' }}>
        <div className="absolute inset-0 animate-pulse" style={{
          background: 'linear-gradient(90deg, transparent, oklch(0.65 0.32 280 / 0.04), transparent)',
          backgroundSize: '200% 100%',
          animation: 'shimmer 1.8s infinite',
        }} />
        {/* Placeholder shape */}
        <div className="absolute inset-0 flex items-center justify-center">
          <div
            className="w-20 h-32 rounded-lg opacity-20 animate-pulse"
            style={{ background: 'var(--muted)' }}
          />
        </div>
      </div>
      <div className="p-4 space-y-3">
        <Skeleton className="h-4 w-3/4" />
        <Skeleton className="h-3 w-full" />
        <Skeleton className="h-3 w-2/3" />
        <div className="flex justify-between items-center pt-1">
          <div className="flex gap-1">
            {[...Array(5)].map((_, i) => (
              <Skeleton key={i} className="w-4 h-4 rounded-full" />
            ))}
          </div>
          <Skeleton className="h-5 w-20" />
        </div>
      </div>
    </div>
  );
}

export default function FigurineGrid({ onAuthRequired }: FigurineGridProps) {
  const [activeCategory, setActiveCategory] = useState('all');
  const { categories } = useCategories();
  const { figurines, loading } = useFigurines(activeCategory);
  const sectionRef = useRef<HTMLElement>(null);

  return (
    <section ref={sectionRef} id="kolekcja" className="py-20 px-4 max-w-7xl mx-auto">
      {/* Section header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
        className="mb-10"
      >
        <div className="flex items-center gap-3 mb-3">
          <div
            className="w-1 h-8 rounded-full"
            style={{ background: 'linear-gradient(to bottom, var(--primary), var(--accent))' }}
          />
          <span
            className="text-xs font-bold tracking-widest text-muted-foreground"
            style={{ fontFamily: 'JetBrains Mono, monospace' }}
          >
            KOLEKCJA
          </span>
        </div>
        <h2
          className="text-4xl sm:text-5xl font-black text-foreground mb-2"
          style={{ fontFamily: 'Orbitron, sans-serif' }}
        >
          NASZE FIGURKI
        </h2>
        <p className="text-muted-foreground text-lg" style={{ fontFamily: 'Rajdhani, sans-serif' }}>
          Każda figurka to unikalne dzieło — od projektu AI po wydruk 3D
        </p>
      </motion.div>

      {/* Category filter */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5, delay: 0.1 }}
        className="mb-8"
      >
        <CategoryFilter
          categories={categories}
          active={activeCategory}
          onChange={setActiveCategory}
        />
      </motion.div>

      {/* Grid */}
      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {[...Array(8)].map((_, i) => <FigurineSkeletonCard key={i} />)}
        </div>
      ) : figurines.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-24 text-muted-foreground">
          <Package className="w-16 h-16 mb-4 opacity-30" />
          <p style={{ fontFamily: 'Orbitron, sans-serif', fontSize: '0.9rem' }}>
            Brak figurek w tej kategorii
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {figurines.map((fig, i) => (
            <FigurineCard
              key={fig.id}
              figurine={fig}
              index={i}
              onAuthRequired={onAuthRequired}
            />
          ))}
        </div>
      )}

      {/* Shimmer CSS */}
      <style>{`
        @keyframes shimmer {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }
      `}</style>
    </section>
  );
}
